import React, { useState, useEffect, useCallback } from 'react';
import './AdHocForecastControl.css';
import { calculateAdjustment } from '../../utils_1/forecastUtils';
import HelpTooltip from '../HelpTooltip/HelpTooltip';
import useDimensionOptions from '../../hooks/useDimensionOptions';
import usePersistentState from '../../hooks/usePersistentState';
import useSharedForecastFilters from '../../hooks/useSharedForecastFilters';
import { useAuth } from '../../context/AuthContext';

const AdHocForecastControl = ({
  onAdHocAdjustment,
  loading,
  onFetchCurrentForecast,
  currentForecastLoading,
  suggestedForecast,        // numeric value
  forecastMethodLabel,      // e.g. "Day-of-Week Pattern"
  forecastMethodKey,        // e.g. "day_of_week_pattern"
}) => {
  // Ad-hoc changes above the threshold trigger admin approval for non-admin roles.
  const defaultParams = {
    brand: '',
    sku_id: '',
    channel: '',
    location: '',
    target_date: (() => {
      const tomorrow = new Date();
      tomorrow.setDate(tomorrow.getDate() + 1);
      return tomorrow.toISOString().split('T')[0];
    })(),
    adjustment_type: 'absolute',
    adjustment_value: '',
    current_forecast: '',
    reason_category: 'demand_shift',
    notes: '',
    granularity_level: 'sku_channel_location'
  };
  const [params, setParams] = usePersistentState('adhoc_filters', defaultParams);
  const [sharedFilters, setSharedFilters] = useSharedForecastFilters(['adhoc_filters']);

  const [previewValue, setPreviewValue] = useState(null);
  const [valueWarning, setValueWarning] = useState('');
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const sharedBrand = sharedFilters?.brand ?? '';
  const sharedSkuId = sharedFilters?.sku_id ?? '';
  const sharedChannel = sharedFilters?.channel ?? '';
  const sharedLocation = sharedFilters?.location ?? '';

  // Shared dimension options hook
  const { options, selectedBrand, setSelectedBrand } = useDimensionOptions(params.brand || '');

  // â”€â”€ Sync brand from the shared hook â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
  useEffect(() => {
    if (selectedBrand !== params.brand) {
      setParams(prev => ({ ...prev, brand: selectedBrand, sku_id: '' }));
    }
  }, [selectedBrand]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    setParams(prev => {
      const next = { ...prev };
      let changed = false;
      if (next.brand !== sharedBrand) { next.brand = sharedBrand; changed = true; }
      if (next.sku_id !== sharedSkuId) { next.sku_id = sharedSkuId; changed = true; }
      if (next.channel !== sharedChannel) { next.channel = sharedChannel; changed = true; }
      if (next.location !== sharedLocation) { next.location = sharedLocation; changed = true; }
      return changed ? next : prev;
    });
  }, [sharedBrand, sharedSkuId, sharedChannel, sharedLocation, setParams]);

  // â”€â”€ Auto-fetch current forecast whenever key dimensions change â”€â”€â”€â”€â”€â”€â”€â”€
  useEffect(() => {
    if (params.sku_id && params.target_date && onFetchCurrentForecast) {
      // Clear stale value immediately so the user sees the loading state
      setParams(prev => ({ ...prev, current_forecast: '' }));
      setPreviewValue(null);
      onFetchCurrentForecast(params.sku_id, params.channel, params.location, params.target_date);
    }
  }, [params.sku_id, params.channel, params.location, params.target_date]); // eslint-disable-line react-hooks/exhaustive-deps

  // â”€â”€ Populate field whenever backend returns a value (always overwrite) â”€
  useEffect(() => {
    if (suggestedForecast != null && suggestedForecast !== '') {
      setParams(prev => ({ ...prev, current_forecast: String(suggestedForecast) }));
    } else if (suggestedForecast === null && !currentForecastLoading && params.sku_id) {
      setParams(prev => ({ ...prev, current_forecast: '0' }));
    }
  }, [suggestedForecast, currentForecastLoading, params.sku_id]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (params.current_forecast && params.adjustment_value) {
      const metrics = calculateAdjustment(
        params.current_forecast,
        params.adjustment_value,
        params.adjustment_type
      );
      setPreviewValue(metrics ? metrics.newValue : null);
    }
  }, [params.current_forecast, params.adjustment_value, params.adjustment_type]);

  useEffect(() => {
    if (previewValue == null || params.current_forecast === '' || params.adjustment_value === '' || params.adjustment_value == null) {
      setValueWarning('');
      return;
    }

    const numericPreviewValue = Number(previewValue);
    const numericCurrentValue = Number(params.current_forecast);
    const changeAmount = Math.abs(numericPreviewValue - numericCurrentValue);
    if (Number.isFinite(changeAmount) && changeAmount > 10000) {
      setValueWarning(
        isAdmin
          ? 'Change exceeds 10,000.'
          : 'Change exceeds 10,000. Admin approval will be required.'
      );
    } else {
      setValueWarning('');
    }
  }, [previewValue, params.current_forecast, params.adjustment_value, isAdmin]);

  // ── Recalculate adjustment preview ───────────────────────────────────────
  const handleChange = useCallback((param, value) => {
    setParams(prev => {
      const newParams = { ...prev, [param]: value };

      // Auto-calculate preview when adjustment values change
      if (
        (param === 'adjustment_value' || param === 'current_forecast' || param === 'adjustment_type') &&
        newParams.current_forecast && newParams.adjustment_value
      ) {
        const metrics = calculateAdjustment(
          newParams.current_forecast,
          newParams.adjustment_value,
          newParams.adjustment_type
        );
        setPreviewValue(metrics ? metrics.newValue : null);
      } else if (!newParams.current_forecast || !newParams.adjustment_value) {
        setPreviewValue(null);
      }

      return newParams;
    });
    if (['brand', 'sku_id', 'channel', 'location'].includes(param)) {
      setSharedFilters(prev => ({ ...prev, [param]: value }));
    }
  }, [setParams, setSharedFilters]);

  const handleBrandChange = useCallback((value) => {
    handleChange('brand', value);
    setSelectedBrand(value);
  }, [handleChange, setSelectedBrand]);

  const isFormValid = () =>
    params.sku_id &&
    params.target_date &&
    params.current_forecast !== '' &&
    params.adjustment_value &&
    !isNaN(params.current_forecast) &&
    !isNaN(params.adjustment_value);

  const currentNumericValue = Number(params.current_forecast);
  const previewNumericValue = Number(previewValue);
  const requiresAdminApproval = !isAdmin &&
    Number.isFinite(currentNumericValue) &&
    Number.isFinite(previewNumericValue) &&
    Math.abs(previewNumericValue - currentNumericValue) > 10000;

  return (
    <div className="control-panel card">
      <div className="panel-header">
        <h3 className="panel-title">Ad-Hoc Forecast Adjustments</h3>
      </div>
      <div className="tab-content">
        <div className="form-grid grid-3">

          {/* Brand */}
          <div className="form-group">
            <label className="form-label" style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
              Brand <HelpTooltip content="Select a brand to narrow down the SKU list below." />
            </label>
            <select
              value={params.brand}
              onChange={e => handleBrandChange(e.target.value)}
              className="form-input"
            >
              <option value="">All Brands (Optional)</option>
              {options.brands.map(brand => (
                <option key={brand} value={brand}>{brand}</option>
              ))}
            </select>
          </div>

          {/* SKU */}
          <div className="form-group">
            <label className="form-label" style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
              SKU ID <HelpTooltip content="Select a specific product to adjust. Pick a Brand first to filter this list." />
            </label>
            <select
              value={params.sku_id}
              onChange={e => handleChange('sku_id', e.target.value)}
              className="form-input"
              required
            >
              <option value="">Select SKU</option>
              {options.skus.map(sku => (
                <option key={sku} value={sku}>{sku}</option>
              ))}
            </select>
          </div>

          {/* Channel */}
          <div className="form-group">
            <label className="form-label" style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
              Channel <HelpTooltip content="Select a specific sales channel, or leave as 'All Channels'." />
            </label>
            <select
              value={params.channel}
              onChange={e => handleChange('channel', e.target.value)}
              className="form-input"
            >
              <option value="">All Channels (Optional)</option>
              {options.channels.map(ch => (
                <option key={ch} value={ch}>{ch}</option>
              ))}
            </select>
          </div>

          {/* Location */}
          <div className="form-group">
            <label className="form-label" style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
              Location <HelpTooltip content="Select a specific geographical location, or leave as 'All Locations'." />
            </label>
            <select
              value={params.location}
              onChange={e => handleChange('location', e.target.value)}
              className="form-input"
            >
              <option value="">All Locations (Optional)</option>
              {options.locations.map(loc => (
                <option key={loc} value={loc}>{loc}</option>
              ))}
            </select>
          </div>

          {/* Target Date */}
          <div className="form-group">
            <label className="form-label" style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
              Target Date <HelpTooltip content="The specific date for which you want to adjust the forecast." />
            </label>
            <input
              type="date"
              value={params.target_date}
              onChange={e => handleChange('target_date', e.target.value)}
              className="form-input"
              required
            />
          </div>



          {/* Granularity */}
          <div className="form-group">
            <label className="form-label" style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
              Granularity <HelpTooltip content="Choose the level of detail for the forecast output." />
            </label>
            <select
              value={params.granularity_level}
              onChange={e => handleChange('granularity_level', e.target.value)}
              className="form-input"
            >
              <option value="sku_channel_location">SKU + Channel + Location</option>
              <option value="sku_channel">SKU + Channel</option>
              <option value="sku">SKU Only</option>
            </select>
          </div>

          {/* Adjustment Type */}
          <div className="form-group">
            <label className="form-label" style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
              Adjustment Type <HelpTooltip content="Select how the adjustment value will be applied." />
            </label>
            <select
              value={params.adjustment_type}
              onChange={e => handleChange('adjustment_type', e.target.value)}
              className="form-input"
            >
              <option value="absolute">Absolute Value</option>
              <option value="percentage">Percentage Change</option>
              <option value="multiplier">Multiplier</option>
            </select>
          </div>

          {/* Adjustment Value */}
          <div className="form-group">
            <label className="form-label" style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
              Adjustment Value
              {params.adjustment_type === 'percentage' && ' (%)'}
              {params.adjustment_type === 'multiplier' && ' (x)'}
              <HelpTooltip content="The numeric value to apply based on the selected adjustment type." />
            </label>
            <input
              type="number"
              value={params.adjustment_value}
              onChange={e => handleChange('adjustment_value', e.target.value)}
              className="form-input"
              min="0"
              placeholder={
                params.adjustment_type === 'absolute' ? 'New forecast value' :
                  params.adjustment_type === 'percentage' ? 'e.g., 15 for +15%' :
                    'e.g., 1.2 for 20% increase'
              }
              step={params.adjustment_type === 'multiplier' ? '0.1' : '1'}
              required
            />
            {valueWarning && (
              <div className="ad-hoc-value-warning" role="alert" aria-live="polite">
                ⚠️ {valueWarning}
              </div>
            )}
          </div>

          {/* Reason Category */}
          <div className="form-group">
            <label className="form-label" style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
              Reason Category <HelpTooltip content="Categorize the underlying reason for making this manual adjustment." />
            </label>
            <select
              value={params.reason_category}
              onChange={e => handleChange('reason_category', e.target.value)}
              className="form-input"
            >
              <option value="demand_shift">Demand Shift</option>
              <option value="market_event">Market Event</option>
              <option value="supply_constraint">Supply Constraint</option>
              <option value="promotional_impact">Promotional Impact</option>
              <option value="seasonal_adjustment">Seasonal Adjustment</option>
              <option value="external_factor">External Factor</option>
              <option value="data_correction">Data Correction</option>
              <option value="other">Other</option>
            </select>
          </div>
        </div>

        {/* Adjustment Preview */}
        {previewValue !== null && (
          <div className="adjustment-preview">
            <h4>Adjustment Preview</h4>
            <div className="preview-row">
              <span className="current-value">
                Current: {currentForecastLoading ? '...' : (params.current_forecast === '0' && suggestedForecast === null) ? 'N/A' : params.current_forecast}
              </span>
              <span className="arrow">→</span>
              <span className="new-value">New: {previewValue}</span>
              <span className="change-value">
                ({previewValue > parseFloat(params.current_forecast) ? '+' : ''}
                {Math.round((previewValue - parseFloat(params.current_forecast || 0)) * 100) / 100})
              </span>
            </div>
          </div>
        )}

        {/* Notes */}
        <div className="form-group full-width">
          <label className="form-label" style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
            Notes <HelpTooltip content="Provide additional qualitative context or justification for this adjustment." />
          </label>
          <textarea
            value={params.notes}
            onChange={e => handleChange('notes', e.target.value)}
            className="form-input"
            placeholder="Optional: Provide additional context for this adjustment..."
            rows="3"
          />
        </div>

        <div className="form-actions">
          <button
            onClick={() => onAdHocAdjustment(params)}
            className="btn btn-primary"
            disabled={loading || !isFormValid()}
          >
            {loading ? 'Applying…' : (requiresAdminApproval ? 'Submit for Admin Approval' : 'Apply Adjustment Immediately')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AdHocForecastControl;

